import { ProductStatus } from '@lyka-pet-food/contracts'
import type { ExtraProduct, MealProduct } from '@repo/api-types'

/**
 * Find the meal owning the given variant ID within a meals array.
 *
 * A pure lookup over a meals list (no app state) — call it from plain utils
 * by passing the catalog in, or via `useProducts().getMealByVariantId` which
 * binds the reactive catalog for you.
 *
 * `allowInvisible` is enabled when showing an order line item — a previously
 * purchased product may now be hidden from the catalog but still needs a
 * displayable name. Disable it when listing products for selection.
 */
export function findMealByVariantId(meals: MealProduct[], id: string, { allowInvisible = false } = {}) {
  return meals.find(
    (meal) =>
      (meal.isVisible || allowInvisible) &&
      meal.variants.some((variant) => variant.id === id && (variant.isVisible || allowInvisible)),
  )
}

/**
 * Compare two meal products for sorting purposes based on their sortIndex.
 */
type Sortable = { sortIndex: number }
export function compareMealPosition(a?: Sortable, b?: Sortable) {
  // If sort_index is not defined, we want to show the product last
  if (!a?.sortIndex) return -1
  if (!b?.sortIndex) return 1

  return a.sortIndex - b.sortIndex
}

/**
 * Compare two extra products (treats or supplements) for sorting purposes.
 */
type ComparableExtraProduct = Pick<ExtraProduct, 'name' | 'isNew' | 'status'>
export function compareExtrasProducts(a: ComparableExtraProduct, b: ComparableExtraProduct) {
  // Out of stock last
  if (a.status !== ProductStatus.OUT_OF_STOCK && b.status === ProductStatus.OUT_OF_STOCK) return -1
  if (a.status === ProductStatus.OUT_OF_STOCK && b.status !== ProductStatus.OUT_OF_STOCK) return 1
  // New products first
  if (a.isNew && !b.isNew) return -1
  if (!a.isNew && b.isNew) return 1
  // Alphabetical by name
  return a.name.localeCompare(b.name)
}

type VisibleProductStatus = typeof ProductStatus.ACTIVE | typeof ProductStatus.OUT_OF_STOCK
export function isProductVisible<T extends { status: ProductStatus; isVisible: boolean }>(
  product: T,
): product is T & { status: VisibleProductStatus; isVisible: true } {
  return (product.status === ProductStatus.ACTIVE || product.status === ProductStatus.OUT_OF_STOCK) && product.isVisible
}

export function isVariantVisible<T extends { isVisible?: boolean; status?: ProductStatus }>(variant: T): boolean {
  return (
    !!variant.isVisible && (variant.status === ProductStatus.ACTIVE || variant.status === ProductStatus.OUT_OF_STOCK)
  )
}

/** Variants that are visible for display/selection (isVisible and status ACTIVE or OUT_OF_STOCK). */
export function getVisibleVariants<T extends { isVisible?: boolean; status?: ProductStatus }>(product: {
  variants?: T[]
}): T[] {
  return product.variants?.filter(isVariantVisible) ?? []
}
